/**
 * AWARE Health Frontend — Forecast API Hooks
 *
 * React Query v5 hooks for fetching forecast data from the FastAPI BFF.
 * All requests go through the Vite proxy (/api → backend:8000),
 * so no cross-origin issues in development.
 *
 * React Query v5 note: use `isPending` (not `isLoading`) for initial
 * load state on queries that have no cached data yet.
 */

import axios from 'axios'
import { useQuery } from '@tanstack/react-query'
import type { ForecastDocument, ForecastListItem } from '../types/forecast'

const API_BASE = '/api/v1'

// ---------------------------------------------------------------------------
// Axios instance
// ---------------------------------------------------------------------------
const api = axios.create({
  baseURL: API_BASE,
  headers: { 'Content-Type': 'application/json' },
  timeout: 15_000,
})

// ---------------------------------------------------------------------------
// Query key factory (centralises cache key management)
// ---------------------------------------------------------------------------
export const forecastKeys = {
  all: ['forecasts'] as const,
  list: (country?: string) => [...forecastKeys.all, 'list', country] as const,
  detail: (country: string, issueDate: string) =>
    [...forecastKeys.all, 'detail', country, issueDate] as const,
}

// ---------------------------------------------------------------------------
// Hook: useForecastList
// ---------------------------------------------------------------------------
/**
 * Lists available forecast documents in S3.
 * Optionally filter by country code (e.g. 'VN').
 */
export function useForecastList(country?: string) {
  return useQuery({
    queryKey: forecastKeys.list(country),
    queryFn: async (): Promise<ForecastListItem[]> => {
      const params = country ? { country } : {}
      const { data } = await api.get<ForecastListItem[]>('/forecasts', { params })
      return data
    },
  })
}

// ---------------------------------------------------------------------------
// Hook: useForecast
// ---------------------------------------------------------------------------
/**
 * Fetches the full forecast document for a country + issue date.
 *
 * @param country    - Country code, e.g. 'VN'
 * @param issueDate  - Issue date string, e.g. '2026-W31'
 * @param enabled    - Set to false to skip fetching (e.g. before user selects a region)
 */
export function useForecast(
  country: string | null,
  issueDate: string | null,
  enabled = true,
) {
  return useQuery({
    queryKey: forecastKeys.detail(country ?? '', issueDate ?? ''),
    queryFn: async (): Promise<ForecastDocument> => {
      if (!country || !issueDate) throw new Error('country and issueDate are required')
      const { data } = await api.get<ForecastDocument>(`/forecasts/${country}/${issueDate}`)
      return data
    },
    enabled: enabled && !!country && !!issueDate,
  })
}